import * as React from "react";
import { cn } from "../lib/utils";

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /**
   * Maximum allowed character count. When set, renders a "X / max" character
   * counter below the textarea. Counter color shifts to warning at ≥90% of
   * limit and to error at the limit per UX-DR25 form patterns.
   */
  maxCharacters?: number;
}

/**
 * Textarea — used for hello-text (UX-DR3 + UX-DR25), dossier descriptions,
 * rejection reasons, etc. Multiline input with optional character counter.
 */
export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, maxCharacters, value, defaultValue, onChange, ...props }, ref) => {
    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = React.useState(
      defaultValue?.toString() ?? ""
    );
    const currentValue = isControlled ? value?.toString() ?? "" : internalValue;
    const length = currentValue.length;

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (!isControlled) setInternalValue(e.target.value);
      onChange?.(e);
    };

    const counterColor = maxCharacters
      ? length >= maxCharacters
        ? "text-state-error-fill"
        : length >= maxCharacters * 0.9
          ? "text-state-warning-fill"
          : "text-muted-foreground"
      : "text-muted-foreground";

    return (
      <div className="w-full">
        <textarea
          className={cn(
            // Story 7.1 — added `text-foreground` so typed text remains
            // legible on `bg-background` regardless of parent context
            // (matches the same fix applied to <Input>).
            "flex min-h-[80px] w-full rounded border border-input bg-background text-foreground px-3 py-2 text-sm font-body ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
          ref={ref}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          maxLength={maxCharacters}
          {...props}
        />
        {maxCharacters && (
          <div
            className={cn(
              "mt-1 text-right text-xs font-mono tabular-nums",
              counterColor
            )}
            aria-live="polite"
          >
            {length} / {maxCharacters}
          </div>
        )}
      </div>
    );
  }
);
Textarea.displayName = "Textarea";
